'use client';

import clsx from 'clsx';
import { useRef, type ForwardedRef } from 'react';
import { Button as RACButton } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { IconCheckCircle, IconCopy } from '../../icons/index.js';
import { withStyleProps } from '../../with-style-props.js';
import type { ActionButtonProps } from '../button/types.js';
import { Tooltip } from '../tooltip/tooltip.js';
import type { AriaLabelingProps, CommonProps, StringLikeChildren } from '../types.js';
import type { TextStyleOptions } from '../typography/types.js';
import { textStyle } from '../typography/utils.js';
import { useCopyToClipboard } from './use-copy.js';

// Used to prevent word wrap between icons and text
const WORD_JOINER = '\u2060';

export interface CopyInlineProps
  extends
    CommonProps,
    AriaLabelingProps,
    Pick<TextStyleOptions, 'variant'>,
    Pick<ActionButtonProps, 'isDisabled' | 'onHoverStart' | 'onHoverEnd'> {
  /** The content to be copied. */
  children: StringLikeChildren;
  /** A handler that gets called when the user triggers the copying. */
  onCopy?: () => void;
}

function CopyInline(
  { children, UNSAFE_className, UNSAFE_style, variant, onCopy, ...props }: CopyInlineProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const tooltipTriggerRef = useRef<HTMLSpanElement>(null);
  const [copyStatus, showCopyFeedback, copyToClipboard] = useCopyToClipboard();

  const textValue =
    typeof children === 'string' ? children : typeof children === 'number' ? String(children) : children.join('');

  const messages = useLocalizedMessages('copyToClipboard');

  return (
    <Tooltip
      isOpen={showCopyFeedback}
      isDisabled={props.isDisabled}
      label={copyStatus?.success ? messages.format('success') : messages.format('failure')}
      placement="top"
      triggerRef={tooltipTriggerRef}
    >
      <RACButton
        {...props}
        ref={ref}
        className={clsx('cim-copy-inline', textStyle({ variant, tone: 'base' }), UNSAFE_className)}
        style={UNSAFE_style}
        onPress={() => {
          copyToClipboard(textValue);
          onCopy?.();
        }}
      >
        <span className="cim-copy-inline-text">{children}</span>
        {WORD_JOINER}
        <span className="cim-copy-inline-icon" ref={tooltipTriggerRef}>
          {showCopyFeedback ? <IconCheckCircle /> : <IconCopy />}
        </span>
      </RACButton>
    </Tooltip>
  );
}

/**
 * Allows copying an inline value to clipboard.
 *
 * See [copy inline usage guidelines](https://ui.cimpress.io/components/copy-inline/).
 */
const _CopyInline = withStyleProps(forwardRef(CopyInline), 'CopyInline');

export { _CopyInline as CopyInline };
